import { Hammer, Store } from 'lucide-react';
import torusStill from '@/assets/brand/torus.webp';
import { KorzaTorus } from '@/components/brand/korza-torus/korza-torus';
import type { TenantDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

export interface AppHubHeroProps {
  tenant: Pick<
    TenantDto,
    'name' | 'iconUrl' | 'storeWebsiteDomain' | 'isAppUnderConstruction'
  >;
  /** On a phone the shell's header already shows the title and the store. */
  compact: boolean;
}

function StatusPill({ underConstruction }: { underConstruction: boolean }) {
  const { LL } = useI18nContext();

  return underConstruction ? (
    <span className="inline-flex h-7 shrink-0 items-center gap-[7px] rounded-full px-3 text-xs font-semibold text-[#15191E] shadow-[inset_0_0_0_1px_rgba(21,25,30,0.2)]">
      <Hammer aria-hidden strokeWidth={1.6} className="size-[13px]" />
      {LL.appHub.status.underConstruction()}
    </span>
  ) : (
    <span className="inline-flex h-7 shrink-0 items-center gap-[7px] rounded-full bg-[#15191E] px-3 text-xs font-semibold text-white">
      <span aria-hidden className="size-1.5 rounded-full bg-[#6FA0FF]" />
      {LL.appHub.status.published()}
    </span>
  );
}

/**
 * Top of the "Seu app" hub, in the brand book's language: blue eyebrow, the
 * large title, the store and whether its app is live, and the live 3D torus
 * whole in its own box at the right, never under any text.
 */
export function AppHubHero({ tenant, compact }: AppHubHeroProps) {
  const { LL } = useI18nContext();
  const torusFallback = (
    <img
      src={torusStill}
      alt=""
      aria-hidden="true"
      className="size-full object-contain"
    />
  );

  return (
    <div
      className={cn(
        'flex items-center',
        compact ? 'gap-2 pb-1.5 pt-[18px]' : 'min-h-[260px] gap-8'
      )}
    >
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          compact ? 'items-start gap-3' : 'gap-5'
        )}
      >
        <div className="flex items-center gap-3 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.16em] text-[#155DFC] md:gap-3.5 md:text-xs">
          <span>{LL.appHub.title()}</span>
          <span aria-hidden className="h-px w-6 bg-[#155DFC]/35 md:w-16" />
          <span>{LL.appHub.platforms()}</span>
        </div>

        {compact ? (
          <StatusPill underConstruction={tenant.isAppUnderConstruction} />
        ) : (
          <>
            <h1 className="text-[64px] font-medium leading-[1.02] tracking-[-0.035em] text-[#15191E]">
              {LL.appHub.title()}
            </h1>
            <div className="flex flex-wrap items-center gap-3.5">
              {tenant.iconUrl ? (
                <img
                  src={tenant.iconUrl}
                  alt=""
                  className="size-12 shrink-0 rounded-xl object-cover shadow-[0_10px_24px_-12px_rgba(21,25,30,0.45),0_0_0_1px_rgba(21,25,30,0.06)]"
                />
              ) : (
                <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white shadow-[0_0_0_1px_rgba(21,25,30,0.08)]">
                  <Store aria-hidden strokeWidth={1.5} className="size-5" />
                </span>
              )}
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-lg font-semibold tracking-[-0.01em] text-[#15191E]">
                  {tenant.name}
                </span>
                {tenant.storeWebsiteDomain && (
                  <span className="text-[13px] text-[#4B5361]">
                    {tenant.storeWebsiteDomain}
                  </span>
                )}
              </span>
              <span className="ml-2">
                <StatusPill underConstruction={tenant.isAppUnderConstruction} />
              </span>
            </div>
          </>
        )}

        {tenant.isAppUnderConstruction && (
          <p className="max-w-[480px] text-[13px] leading-normal text-[#4B5361] md:text-[15px]">
            {LL.appHub.underConstructionNote()}
          </p>
        )}
      </div>

      <div
        aria-hidden="true"
        className={cn(
          'relative shrink-0',
          compact ? 'h-28 w-[136px]' : 'h-[280px] w-[380px] max-lg:hidden'
        )}
      >
        <KorzaTorus
          zoom={0.78}
          className="absolute inset-0 size-full"
          fallback={torusFallback}
        />
      </div>
    </div>
  );
}
